'use client';

import React from 'react';
import { StickyActions } from './sticky-actions';
import { ActionGroup } from './action-group';
import { withDisabledReason } from './disabled-action';

export interface FormActionsProps {
  unsaved?: boolean;
  role?: React.AriaRole;
  saving?: boolean;
  primary: React.ReactNode;
  /** Default reason for unavailable actions in either slot. */
  disabledReason?: string;
  /** Overrides the default reason for unavailable primary actions. */
  primaryDisabledReason?: string;
  secondary?: React.ReactNode;
  /** Overrides the default reason for unavailable secondary actions. */
  secondaryDisabledReason?: string;
  placement?: 'docked' | 'inline';
}

/** Caller owns submission and errors; status never claims an unconfirmed save. */
export function FormActions({
  unsaved,
  role,
  saving = false,
  primary,
  secondary,
  disabledReason,
  primaryDisabledReason,
  secondaryDisabledReason,
  placement = 'docked',
}: FormActionsProps) {
  const actions = (
    <ActionGroup
      // An explicit per-slot reason wins; an empty string opts that slot out of the shared reason (it is then disabled only
      // by its own props, with no description). Prefer a real reason: disabled controls must explain why.
      primary={withDisabledReason(primary, primaryDisabledReason ?? disabledReason)}
      secondary={withDisabledReason(secondary, secondaryDisabledReason ?? disabledReason)}
    />
  );
  const status = (unsaved !== undefined || saving) && (
    <p
      role="status"
      className="w-full min-w-0 flex-1 sm:w-auto sm:mr-auto break-words font-body text-body text-text-muted [overflow-wrap:anywhere]"
    >
      {saving ? 'Saving changes…' : unsaved ? 'Unsaved changes' : 'No unsaved changes'}
    </p>
  );

  if (placement === 'inline') {
    return (
      <div
        aria-label="Form actions"
        role={role ?? 'group'}
        className="flex min-w-0 flex-wrap items-center justify-end gap-2"
      >
        {status}
        {actions}
      </div>
    );
  }

  return (
    <StickyActions label="Form actions" role={role}>
      {status}
      {actions}
    </StickyActions>
  );
}
